import React, { useRef, useState } from "react";
import styled from "styled-components";
import { semanticColors, fontWeights } from "../../styles/latitudeTheme";
import AdminFileList, { FileListRefType } from "../components/AdminFileList";
import AdminClientList, {
  ClientListRefType,
} from "../components/AdminClientList";
import AdminSupplierList, {
  SupplierListRefType,
} from "../components/AdminSupplierList";
import AdminUserList, { UserListRefType } from "../components/AdminUserList";
import AdminQuarantineList, {
  QuarantineListRefType,
} from "../components/AdminQuarantineList";

const AdminContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const AdminTitle = styled.h1`
  font-size: 28px;
  font-weight: ${fontWeights.bold};
  margin-bottom: 20px;
  color: ${semanticColors.textPrimary};
  border-bottom: 2px solid ${semanticColors.borderLight};
  padding-bottom: 12px;
`;

const AdminDescription = styled.p`
  margin-bottom: 20px;
  color: ${semanticColors.textSecondary};
  line-height: 1.6;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-bottom: 1px solid ${semanticColors.borderLight};
  flex-wrap: wrap;
  gap: 8px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  background: ${(props) =>
    props.$active ? semanticColors.backgroundHover : "transparent"};
  border: none;
  border-bottom: ${(props) =>
    props.$active
      ? `3px solid ${semanticColors.primary}`
      : "3px solid transparent"};
  cursor: pointer;
  font-size: 15px;
  font-weight: ${(props) =>
    props.$active ? fontWeights.medium : fontWeights.regular};
  color: ${(props) =>
    props.$active ? semanticColors.primary : semanticColors.textSecondary};
  transition: all 0.2s;

  &:hover {
    color: ${semanticColors.primary};
    background: ${semanticColors.backgroundHover};
  }
`;

type TabType = "files" | "clients" | "suppliers" | "users" | "quarantine";

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("files");
  const fileListRef = useRef<FileListRefType>(null);
  const clientListRef = useRef<ClientListRefType>(null);
  const supplierListRef = useRef<SupplierListRefType>(null);
  const userListRef = useRef<UserListRefType>(null);
  const quarantineListRef = useRef<QuarantineListRefType>(null);

  return (
    <AdminContainer data-testid="dashboard-container">
      <AdminTitle data-testid="dashboard-title">Admin Dashboard</AdminTitle>
      <AdminDescription data-testid="dashboard-description">
        Manage files, clients, suppliers and users in the system.
      </AdminDescription>

      <TabContainer>
        <Tab
          $active={activeTab === "files"}
          onClick={() => setActiveTab("files")}
          data-testid="admin-tab-files"
        >
          File Management
        </Tab>
        <Tab
          $active={activeTab === "clients"}
          onClick={() => setActiveTab("clients")}
          data-testid="admin-tab-clients"
        >
          Client Management
        </Tab>
        <Tab
          $active={activeTab === "suppliers"}
          onClick={() => setActiveTab("suppliers")}
          data-testid="admin-tab-suppliers"
        >
          Supplier Management
        </Tab>
        <Tab
          $active={activeTab === "users"}
          onClick={() => setActiveTab("users")}
          data-testid="admin-tab-users"
        >
          User & Access Management
        </Tab>
        <Tab
          $active={activeTab === "quarantine"}
          onClick={() => setActiveTab("quarantine")}
          data-testid="admin-tab-quarantine"
        >
          Quarantine Management
        </Tab>
      </TabContainer>

      {activeTab === "files" && (
        <>
          <AdminDescription>
            View and manage files that have been uploaded to the system. You can
            download files by clicking on their names or delete them using the
            delete button.
          </AdminDescription>
          <AdminFileList ref={fileListRef} />
        </>
      )}

      {activeTab === "clients" && (
        <>
          <AdminDescription>
            Create, update, and delete clients in the system. Note that clients
            with existing files cannot be deleted.
          </AdminDescription>
          <AdminClientList ref={clientListRef} />
        </>
      )}

      {activeTab === "suppliers" && (
        <>
          <AdminDescription>
            Create, update, and delete suppliers in the system. Each supplier is
            associated with a client and can only access their assigned
            subfolder.
          </AdminDescription>
          <AdminSupplierList ref={supplierListRef} />
        </>
      )}

      {activeTab === "users" && (
        <>
          <AdminDescription>
            Manage users, their roles and access permissions. Generate access
            links for customers, suppliers and Portway employees.
          </AdminDescription>
          <AdminUserList ref={userListRef} />
        </>
      )}

      {activeTab === "quarantine" && (
        <>
          <AdminDescription>
            Monitor files in quarantine. View malware scan results.
          </AdminDescription>
          <AdminQuarantineList ref={quarantineListRef} />
        </>
      )}
    </AdminContainer>
  );
};

export default AdminPage;
